/**
 * BowfastMap — production map component.
 *
 * Rendering stack:
 *   - MapLibre GL 5 — WebGL vector map renderer
 *   - PMTiles (via `pmtiles` adapter) — self-hosted vector tiles on Cloudflare R2
 *   - OpenSeaMap raster overlay — buoys, seamarks, depth areas
 *
 * Env var: VITE_PMTILES_URL — full URL to your .pmtiles file on R2
 *   e.g. "https://tiles.bowfast.dev/europe.pmtiles"
 *
 * Falls back to OpenStreetMap raster tiles when the env var is not set.
 */

import * as maplibregl from "maplibre-gl";
import { Protocol } from "pmtiles";
import { useEffect, useRef } from "react";

// ── Config ────────────────────────────────────────────────────────────────────

const PMTILES_URL = import.meta.env.VITE_PMTILES_URL as string | undefined;
const USE_VECTOR = !!PMTILES_URL;
const GLYPHS = "https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf";

// ── Abyss palette constants ───────────────────────────────────────────────────

const C = {
  deepBg: "#060b16",
  ocean: "#0a1628",
  oceanFill: "#071220",
  oceanLine: "#0d2040",
  land: "#162233",
  landLight: "#1a2840",
  landRes: "#1e2d48",
  park: "#15302a",
  forest: "#123028",
  building: "#1f3050",
  buildingOut: "#283a5a",
  roadMinor: "#1c2e4a",
  roadMajor: "#263e5c",
  boundary: "#2a4570",
  coastline: "#047cc7",
  coastGlow: "#0a5a9e",
  cityLabel: "#e8f4fa",
  cityHalo: "#0a1628",
  townLabel: "#b8dff0",
  villageLabel: "#7eb8d4",
  primary: "#047cc7",
  teal: "#14b8a6",
  amber: "#f59e0b",
};

// ── Vector style (PMTiles) ────────────────────────────────────────────────────

function buildVectorStyle(pmtilesUrl: string): object {
  return {
    version: 8,
    glyphs: GLYPHS,
    sources: {
      protomaps: {
        type: "vector",
        url: "pmtiles://" + pmtilesUrl,
        attribution:
          "© <a href='https://protomaps.com'>Protomaps</a> © <a href='https://openstreetmap.org/copyright'>OSM</a>",
      },
      openseamap: {
        type: "raster",
        tiles: ["https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png"],
        tileSize: 256,
        attribution: "© <a href='https://www.openseamap.org'>OpenSeaMap</a>",
        minzoom: 5,
        maxzoom: 18,
      },
    },
    layers: [
      // Background — deep ocean void
      { id: "background", type: "background", paint: { "background-color": C.deepBg } },

      // Earth — slate blue, distinct from ocean
      { id: "earth", type: "fill", source: "protomaps", "source-layer": "earth", paint: { "fill-color": C.land } },

      // Water
      {
        id: "water",
        type: "fill",
        source: "protomaps",
        "source-layer": "water",
        paint: { "fill-color": C.oceanFill, "fill-outline-color": C.oceanLine },
      },

      // Land use — tonal variation gives land texture
      {
        id: "landuse-residential",
        type: "fill",
        source: "protomaps",
        "source-layer": "landuse",
        filter: ["in", ["get", "kind"], ["literal", ["residential", "suburb", "neighbourhood"]]],
        paint: { "fill-color": C.landRes, "fill-opacity": 0.7 },
      },
      {
        id: "landuse-park",
        type: "fill",
        source: "protomaps",
        "source-layer": "landuse",
        filter: ["in", ["get", "kind"], ["literal", ["park", "nature_reserve"]]],
        paint: { "fill-color": C.park, "fill-opacity": 0.8 },
      },
      {
        id: "landuse-forest",
        type: "fill",
        source: "protomaps",
        "source-layer": "landuse",
        filter: ["==", ["get", "kind"], "forest"],
        paint: { "fill-color": C.forest, "fill-opacity": 0.8 },
      },

      // Buildings — subtle structure at high zoom
      {
        id: "buildings",
        type: "fill",
        source: "protomaps",
        "source-layer": "buildings",
        minzoom: 13,
        paint: {
          "fill-color": C.building,
          "fill-opacity": ["interpolate", ["linear"], ["zoom"], 13, 0, 15, 0.6],
          "fill-outline-color": C.buildingOut,
        },
      },

      // Roads — cool blue tones, not green
      {
        id: "roads-minor",
        type: "line",
        source: "protomaps",
        "source-layer": "roads",
        minzoom: 11,
        filter: ["in", ["get", "kind"], ["literal", ["minor_road", "service"]]],
        paint: {
          "line-color": C.roadMinor,
          "line-width": ["interpolate", ["linear"], ["zoom"], 11, 0.3, 16, 1.5],
        },
      },
      {
        id: "roads-major",
        type: "line",
        source: "protomaps",
        "source-layer": "roads",
        minzoom: 7,
        filter: ["in", ["get", "kind"], ["literal", ["primary", "secondary", "trunk", "motorway"]]],
        paint: {
          "line-color": C.roadMajor,
          "line-width": ["interpolate", ["linear"], ["zoom"], 7, 0.4, 14, 2.5],
        },
      },

      // Admin boundaries — subtle country/region borders give shape
      {
        id: "boundaries-country",
        type: "line",
        source: "protomaps",
        "source-layer": "boundaries",
        filter: ["==", ["get", "kind"], "country"],
        paint: { "line-color": C.boundary, "line-width": 1, "line-dasharray": [4, 2], "line-opacity": 0.6 },
      },
      {
        id: "boundaries-region",
        type: "line",
        source: "protomaps",
        "source-layer": "boundaries",
        filter: ["==", ["get", "kind"], "region"],
        minzoom: 6,
        paint: { "line-color": C.boundary, "line-width": 0.5, "line-dasharray": [2, 3], "line-opacity": 0.35 },
      },

      // Coastline — outer glow + bright inner line
      {
        id: "coastline-glow",
        type: "line",
        source: "protomaps",
        "source-layer": "water",
        paint: { "line-color": C.coastGlow, "line-width": 4, "line-opacity": 0.2, "line-blur": 3 },
      },
      {
        id: "coastline",
        type: "line",
        source: "protomaps",
        "source-layer": "water",
        paint: { "line-color": C.coastline, "line-width": 1.2, "line-opacity": 0.6 },
      },

      // OpenSeaMap nautical overlay — invert-bright for dark basemap
      // The raster has black text on transparent bg — we need to lift black → light
      {
        id: "openseamap",
        type: "raster",
        source: "openseamap",
        paint: {
          "raster-opacity": 0.95,
          "raster-brightness-min": 0.7,
          "raster-brightness-max": 1.0,
          "raster-contrast": 0.3,
          "raster-saturation": 0.5,
        },
      },

      // ── Labels (above everything) ──────────────────────────────────

      // Cities — bright, bold, with glow halo
      {
        id: "places-city",
        type: "symbol",
        source: "protomaps",
        "source-layer": "places",
        filter: ["==", ["get", "kind"], "city"],
        layout: {
          "text-field": ["get", "name"],
          "text-font": ["Noto Sans Bold"],
          "text-size": ["interpolate", ["linear"], ["zoom"], 5, 11, 10, 15],
          "text-anchor": "center",
          "text-padding": 8,
        },
        paint: {
          "text-color": C.cityLabel,
          "text-halo-color": C.cityHalo,
          "text-halo-width": 2,
          "text-halo-blur": 1,
        },
      },
      // Towns
      {
        id: "places-town",
        type: "symbol",
        source: "protomaps",
        "source-layer": "places",
        filter: ["==", ["get", "kind"], "town"],
        minzoom: 8,
        layout: {
          "text-field": ["get", "name"],
          "text-font": ["Noto Sans Medium"],
          "text-size": ["interpolate", ["linear"], ["zoom"], 8, 10, 13, 13],
          "text-padding": 6,
        },
        paint: {
          "text-color": C.townLabel,
          "text-halo-color": C.cityHalo,
          "text-halo-width": 1.8,
          "text-halo-blur": 0.8,
        },
      },
      // Villages — appear at zoom 11+
      {
        id: "places-village",
        type: "symbol",
        source: "protomaps",
        "source-layer": "places",
        filter: ["in", ["get", "kind"], ["literal", ["village", "hamlet"]]],
        minzoom: 11,
        layout: {
          "text-field": ["get", "name"],
          "text-font": ["Noto Sans Regular"],
          "text-size": 10,
          "text-padding": 4,
        },
        paint: {
          "text-color": C.villageLabel,
          "text-halo-color": C.cityHalo,
          "text-halo-width": 1.5,
          "text-halo-blur": 0.5,
        },
      },
    ],
  };
}

// ── Raster fallback (no PMTiles URL) ──────────────────────────────────────────

function buildRasterStyle(): object {
  return {
    version: 8,
    sources: {
      osm: {
        type: "raster",
        tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
        tileSize: 256,
        attribution: "© <a href='https://www.openstreetmap.org/copyright'>OSM</a>",
        maxzoom: 19,
      },
      openseamap: {
        type: "raster",
        tiles: ["https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png"],
        tileSize: 256,
        attribution: "© <a href='https://www.openseamap.org'>OpenSeaMap</a>",
        minzoom: 5,
        maxzoom: 18,
      },
    },
    layers: [
      { id: "osm", type: "raster", source: "osm" },
      { id: "openseamap", type: "raster", source: "openseamap", paint: { "raster-opacity": 0.9 } },
    ],
  };
}

// ── Public API ────────────────────────────────────────────────────────────────

export interface BowfastMapProps {
  /** [lng, lat] centre */
  center?: [number, number];
  zoom?: number;
  /** GeoJSON LineString coords for the route track */
  route?: [number, number][];
  /** Waypoint markers to place on the map */
  waypoints?: { name: string; coords: [number, number]; kind: "depart" | "waypoint" | "arrive" }[];
  /** Show OpenSeaMap nautical layer */
  showSeamarks?: boolean;
  /** Additional class on the outermost wrapper */
  className?: string;
  /** Height of the map container — defaults to 100% of parent */
  height?: string;
}

export function BowfastMap({
  center = [16.8, 43.35],
  zoom = 7,
  route,
  waypoints,
  showSeamarks = true,
  className,
  height = "100%",
}: BowfastMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);

  // Initialise map once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    // PMTiles protocol — only register once globally
    if (USE_VECTOR) {
      const proto = new Protocol();
      maplibregl.addProtocol("pmtiles", proto.tile);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const style = (USE_VECTOR ? buildVectorStyle(PMTILES_URL!) : buildRasterStyle()) as any;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style,
      center,
      zoom,
      attributionControl: false,
    });

    map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-left");
    map.addControl(new maplibregl.NavigationControl(), "top-right");

    map.on("load", () => {
      // Route line
      if (route && route.length > 1) {
        map.addSource("route", {
          type: "geojson",
          data: {
            type: "Feature",
            geometry: { type: "LineString", coordinates: route },
            properties: {},
          },
        });
        map.addLayer({
          id: "route-glow",
          type: "line",
          source: "route",
          paint: { "line-color": C.primary, "line-width": 8, "line-opacity": 0.15, "line-blur": 4 },
        });
        map.addLayer({
          id: "route-line",
          type: "line",
          source: "route",
          layout: { "line-cap": "round" },
          paint: { "line-color": C.primary, "line-width": 2.5, "line-dasharray": [2, 3] },
        });
      }

      // Waypoint markers — rendered as GeoJSON circle layers (canvas-native, no drift)
      if (waypoints && waypoints.length > 0) {
        const features = waypoints.map((wp) => ({
          type: "Feature" as const,
          geometry: { type: "Point" as const, coordinates: wp.coords },
          properties: { name: wp.name, kind: wp.kind },
        }));

        map.addSource("waypoints", {
          type: "geojson",
          data: { type: "FeatureCollection", features },
        });

        // Outer glow ring
        map.addLayer({
          id: "waypoint-glow",
          type: "circle",
          source: "waypoints",
          paint: {
            "circle-radius": 14,
            "circle-color": ["match", ["get", "kind"], "depart", C.teal, "arrive", C.amber, C.primary],
            "circle-opacity": 0.15,
            "circle-blur": 0.4,
          },
        });

        // Main circle
        map.addLayer({
          id: "waypoint-circle",
          type: "circle",
          source: "waypoints",
          paint: {
            "circle-radius": 8,
            "circle-color": ["match", ["get", "kind"], "depart", C.teal, "arrive", C.amber, C.primary],
            "circle-stroke-width": 2,
            "circle-stroke-color": "rgba(255,255,255,0.35)",
          },
        });

        // Popup on click
        map.on("click", "waypoint-circle", (e) => {
          const f = e.features?.[0];
          if (!f || f.geometry.type !== "Point") return;
          const coords = f.geometry.coordinates as [number, number];
          const { name, kind } = f.properties as { name: string; kind: string };
          // Built from DOM nodes, not HTML: waypoint names are user data.
          const content = document.createElement("div");
          content.style.cssText = "font-family:system-ui;padding:4px 2px";
          const title = content.appendChild(document.createElement("b"));
          title.style.cssText = "font-size:13px;color:#0f172a";
          title.textContent = name;
          const subtitle = content.appendChild(document.createElement("div"));
          subtitle.style.cssText = "font-size:11px;color:#64748b;text-transform:uppercase;letter-spacing:.05em";
          subtitle.textContent = kind;
          new maplibregl.Popup({ offset: 16, closeButton: false }).setLngLat(coords).setDOMContent(content).addTo(map);
        });

        // Pointer cursor on hover
        map.on("mouseenter", "waypoint-circle", () => {
          map.getCanvas().style.cursor = "pointer";
        });
        map.on("mouseleave", "waypoint-circle", () => {
          map.getCanvas().style.cursor = "";
        });
      }

      // Toggle seamarks
      map.setLayoutProperty("openseamap", "visibility", showSeamarks ? "visible" : "none");
    });

    mapRef.current = map;

    return () => {
      map.remove();
      if (USE_VECTOR) maplibregl.removeProtocol("pmtiles");
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // React to showSeamarks prop changes after init
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    map.setLayoutProperty("openseamap", "visibility", showSeamarks ? "visible" : "none");
  }, [showSeamarks]);

  return (
    <div className={className} style={{ position: "relative", height, minHeight: 300 }}>
      <div ref={containerRef} style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }} />
    </div>
  );
}
